<template>
    <div class="options" :class="{ 'has-selected': selectedFrameGids.length > 0 }">
        <div v-for="(gid, index) in selectedFrameGids" :key="gid" class="option">
            <div class="option__header">
                <h3 class="option__title">Экран №{{ index + 1 }}</h3>
                <div class="option__id-and-actions">
                    <label class="option__id" :title="'ID: ' + gid">ID: {{ gid }} &nbsp; · &nbsp;
                        <img src="/images/size.svg" alt="size" />
                        {{ getFrameSize(gid).width }} x
                        {{ getFrameSize(gid).height }}
                    </label>
                </div>
            </div>

            <!-- Предупреждение для неактивных фреймов -->
            <div v-if="!isFrameActive(gid)" class="inactive-warning">
                <img src="/images/info.svg" alt="info" />
                <span>У этого экрана нет ценовых пакетов. Выберите другой экран.</span>
            </div>

            <div class="option__body">
                <div class="tariff-select">
                    <div class="tariff-select__header">
                        Выберите тариф
                        <img v-if="!getSelectedTariffId(gid)" alt="red_star" class="is-empty"
                            src="/images/red_star.svg" />
                    </div>
                    <div class="tariff-cards">
                        <label v-for="t in tariffs[gid] || []" :key="t.id" class="tariff-card">
                            <input type="radio" :name="`tariff-${gid}`" :value="t.id"
                                :checked="getSelectedTariffId(gid) == t.id" @change="onTariffChange(gid, t.id)" />
                            <div class="tariff-card__content">
                                <div class="tariff-card__row">
                                    <span class="tariff-price">{{ formatPrice(t.price) }} / день</span>
                                </div>
                                <div class="tariff-card__row">
                                    <span>{{ t.guaranteedRepetitionPerDay }} показов/день</span>
                                    <span>Требуемая длина креатива: {{ humanizeMs(t.duration) }}</span>
                                </div>
                            </div>
                        </label>
                    </div>
                </div>
                <PeriodOptionSettings :frame="getFrame(gid)" :campaignPeriod="campaignPeriod"
                    @update:frame="updateFrame" />
                <BasicOptionSettings :frame="getFrame(gid)" :tariffs="tariffs[gid]" :campaign-period="campaignPeriod"
                    @update:frame="updateFrame" />
            </div>
        </div>
    </div>
</template>

<script>
import BasicOptionSettings from './optionSettings/BasicOptionSettings.vue';
import PeriodOptionSettings from './optionSettings/PeriodOptionSettings.vue';
import { formatDateOnlyInRtbTZ } from '../../../utils/timezones';

export default {
    name: 'Options',
    components: {
        BasicOptionSettings,
        PeriodOptionSettings,
    },
    props: {
        frames: { type: Array, default: () => [] },
        selectedFrameGids: { type: Array, default: () => [] },
        campaignFrames: { type: Array, default: () => [] },
        campaignPeriod: {
            type: Object,
            default: () => ({ start: '', end: '' }),
        },
    },
    emits: ['update:campaignFrames'],
    data() {
        return {
            tariffs: {},
        };
    },
    mounted() {
        this.hydrateTariffsFromFrames();
    },
    watch: {
        frames: {
            deep: true,
            handler() {
                this.hydrateTariffsFromFrames();
            },
        },
        selectedFrameGids: {
            handler(newGids) {
                // Убираем автоматический выбор дефолтного тарифа
                // Пользователь должен сам выбрать тариф
            },
        },
        campaignPeriod: {
            deep: true,
            handler(newPeriod, oldPeriod) {
                // Если период кампании изменился и есть даты, обновляем даты периодов для всех фреймов
                if (newPeriod.start && newPeriod.end) {
                    this.campaignFrames.forEach((frame) => {
                        // Обновляем даты периодов только если они были установлены из периода кампании
                        // (т.е. если они совпадали со старым периодом кампании)
                        const shouldUpdate = frame.periods.length === 1 &&
                            frame.periods[0].start && frame.periods[0].end &&
                            oldPeriod && oldPeriod.start && oldPeriod.end &&
                            frame.periods[0].start === oldPeriod.start &&
                            frame.periods[0].end === oldPeriod.end;

                        if (shouldUpdate || (frame.periods.length === 0 || !frame.periods[0].start)) {
                            frame.periods = [{ start: newPeriod.start, end: newPeriod.end }];
                        }
                    });

                    this.$emit('update:campaignFrames', [...this.campaignFrames]);
                }

                // Пересчитываем стоимость для всех фреймов
                (this.selectedFrameGids || []).forEach((gid) => {
                    this.recalculateCost(gid);
                });
            },
        },
    },
    methods: {
        formatPrice(n) {
            const num = Number(n || 0);
            return new Intl.NumberFormat('ru-RU', {
                style: 'currency',
                currency: 'KZT',
                currencyDisplay: 'symbol',
                maximumFractionDigits: 2,
            }).format(num);
        },
        humanizeMinutes(min) {
            const m = Number(min || 0);
            if (!Number.isFinite(m) || m <= 0) return `в течение дня`;
            if (m < 60) return `${m} мин`;
            const hrs = Math.floor(m / 60);
            const rem = m % 60;
            return rem ? `${hrs} ч ${rem} мин` : `${hrs} ч`;
        },
        humanizeMs(ms) {
            const s = Math.round(Number(ms || 0) / 1000);
            if (s < 60) return `${s} с`;
            const m = Math.floor(s / 60);
            const rem = s % 60;
            return rem ? `${m} м ${rem} с` : `${m} м`;
        },
        hydrateTariffsFromFrames() {
            (this.frames || []).forEach((f) => {
                const gid = f.gid;
                const arr = f.prices || [];
                // Vue 3: прямое присваивание реактивно
                this.tariffs[gid] = arr;
            });
        },
        getFrame(gid) {
            const normalizedGid = String(gid);
            let inv = this.campaignFrames.find((i) => i.gid === normalizedGid);
            if (!inv) {
                // Автоматически устанавливаем даты периодов равными датам кампании
                const periods = this.campaignPeriod.start && this.campaignPeriod.end
                    ? [{ start: this.campaignPeriod.start, end: this.campaignPeriod.end }]
                    : [{ start: '', end: '' }];

                inv = {
                    gid: normalizedGid,
                    frequency: { repetitions: 1, loopDuration: 60000 },
                    cost: 0,
                    duration: 5000,
                    selectedTariffId: '',
                    periods: periods,
                    timeSettings: [],
                };
                this.campaignFrames.push(inv);

                this.$emit('update:campaignFrames', [...this.campaignFrames]);
            }
            return inv;
        },
        getSelectedTariffId(gid) {
            const frame = this.campaignFrames.find((i) => i.gid === String(gid));
            return frame ? frame.selectedTariffId : '';
        },
        onTariffChange(gid, tariffId) {
            const frame = this.getFrame(gid);
            frame.selectedTariffId = tariffId;

            const arr = this.tariffs[gid] || [];
            const t = arr.find((x) => String(x.id) === String(tariffId));
            if (!t) return;

            // frequency
            // repetitions берем из repetitionPerDayBy (количество показов по прайсу)
            const repetitions = Number(t.repetitionPerDayBy) || 0;
            const durationMs = Number(t.duration) || 0;
            frame.frequency = {
                repetitions,
                // loopDuration всегда будет 86400000 при отправке на API
                loopDuration: 86400000, // день в миллисекундах
            };
            frame.duration = durationMs;

            // Пересчитываем стоимость только если есть полные даты
            this.recalculateCostIfPossible(gid);

            this.updateFrame({ ...frame });
        },
        daysForFrame(gid) {
            // periods по фрейму (массив объектов со start/end ISO)
            const frame = this.getFrame(gid);
            const periods = Array.isArray(frame.periods) ? frame.periods : [];
            if (!periods.length) {
                // если нет индивидуальных периодов — используем общий период кампании
                if (!this.campaignPeriod.start || !this.campaignPeriod.end) return 1;
                const start = new Date(this.campaignPeriod.start);
                const end = new Date(this.campaignPeriod.end);
                // +1 чтобы включить оба дня (начало и конец)
                return Math.max(1, Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1);
            }
            // суммируем дни по всем периодам
            let total = 0;
            periods.forEach((p) => {
                if (!p.start || !p.end) return;
                const s = new Date(p.start);
                const e = new Date(p.end);
                // +1 чтобы включить оба дня (начало и конец)
                const d = Math.max(1, Math.floor((e - s) / (24 * 60 * 60 * 1000)) + 1);
                total += d;
            });
            return Math.max(1, total);
        },
        updateFrame(updatedFrame) {
            const index = this.campaignFrames.findIndex((i) => i.gid === updatedFrame.gid);
            if (index !== -1) {
                this.campaignFrames.splice(index, 1, updatedFrame);

                // Пересчитываем стоимость при изменении периодов
                this.recalculateCost(updatedFrame.gid);

                this.$emit('update:campaignFrames', [...this.campaignFrames]);
            }
        },
        recalculateCostIfPossible(gid) {
            const frame = this.getFrame(gid);
            if (!frame.selectedTariffId) return;

            const tariffs = this.tariffs[gid] || [];
            const tariff = tariffs.find((t) => String(t.id) === String(frame.selectedTariffId));
            if (!tariff) return;

            // Проверяем, есть ли полные даты для пересчета
            const periods = Array.isArray(frame.periods) ? frame.periods : [];
            const hasCompleteDates = periods.some(p => p.start && p.end);

            if (!hasCompleteDates) {
                // Если нет полных дат, используем общий период кампании
                if (!this.campaignPeriod.start || !this.campaignPeriod.end) {
                    frame.cost = 0; // Не пересчитываем, если нет полных дат
                    return;
                }
            }

            // Новый расчет стоимости с учетом часовых слотов
            const calculationResult = this.calculateFrameCost(gid, tariff);
            frame.cost = calculationResult.totalCost;
            frame.baseCost = calculationResult.baseTotalCost;
            frame.costBreakdown = calculationResult.breakdown;
        },
        recalculateCost(gid) {
            const frame = this.getFrame(gid);
            if (!frame.selectedTariffId) return;

            const tariffs = this.tariffs[gid] || [];
            const tariff = tariffs.find((t) => String(t.id) === String(frame.selectedTariffId));
            if (!tariff) return;

            // Новый расчет стоимости с учетом часовых слотов
            const calculationResult = this.calculateFrameCost(gid, tariff);
            frame.cost = calculationResult.totalCost;
            frame.baseCost = calculationResult.baseTotalCost;
            frame.costBreakdown = calculationResult.breakdown;

            // Обновляем frame в массиве campaignFrames
            const index = this.campaignFrames.findIndex((f) => f.gid === gid);
            if (index !== -1) {
                this.campaignFrames.splice(index, 1, frame);
                // Уведомляем родительский компонент об изменениях
                this.$emit('update:campaignFrames', [...this.campaignFrames]);
            }
        },
        calculateFrameCost(gid, tariff) {
            const frame = this.getFrame(gid);
            const dailyPrice = Number(tariff.price) || 0;
            const dailyBasePrice = Number(tariff.basePrice) || dailyPrice;
            const hourlyPrice = dailyPrice / 24; // цена за час
            const hourlyBasePrice = dailyBasePrice / 24; // базовая цена за час (без наценки)

            // Получаем периоды для расчета
            const periods = Array.isArray(frame.periods) ? frame.periods : [];
            let calculationPeriods = periods;

            // Если нет индивидуальных периодов, используем общий период кампании
            if (!periods.length || !periods.some(p => p.start && p.end)) {
                if (!this.campaignPeriod.start || !this.campaignPeriod.end) {
                    return { totalCost: 0, breakdown: [] };
                }
                calculationPeriods = [{
                    start: this.campaignPeriod.start,
                    end: this.campaignPeriod.end
                }];
            }

            const breakdown = [];
            let totalCost = 0;
            let baseTotalCost = 0;

            // Проходим по каждому периоду
            calculationPeriods.forEach((period, periodIndex) => {
                if (!period.start || !period.end) return;

                const startDate = new Date(period.start);
                const endDate = new Date(period.end);

                // Рассчитываем количество дней в периоде
                const daysInPeriod = Math.max(1, Math.floor((endDate - startDate) / (24 * 60 * 60 * 1000)) + 1);

                // Проходим по каждому дню в периоде
                for (let dayOffset = 0; dayOffset < daysInPeriod; dayOffset++) {
                    const currentDate = new Date(startDate);
                    currentDate.setDate(startDate.getDate() + dayOffset);
                    const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay(); // Воскресенье = 7

                    // Ищем настройки времени для этого дня недели
                    const timeSettings = Array.isArray(frame.timeSettings) ? frame.timeSettings : [];
                    const dayTimeSettings = timeSettings.filter(ts => ts.day === dayOfWeek);

                    if (dayTimeSettings.length === 0) {
                        // Если нет настроек времени для этого дня, считаем полный день (24 часа)
                        const dayCost = hourlyPrice * 24;
                        const baseDayCost = hourlyBasePrice * 24;
                        totalCost += dayCost;
                        baseTotalCost += baseDayCost;
                        breakdown.push({
                            day: formatDateOnlyInRtbTZ(currentDate),
                            hours: 24,
                            partialCost: dayCost,
                            periodIndex: periodIndex
                        });
                    } else {
                        // Рассчитываем стоимость для каждого временного слота
                        dayTimeSettings.forEach(timeSetting => {
                            const startTime = Number(timeSetting.startTime) || 0;
                            const endTime = Number(timeSetting.endTime) || 0;

                            // Используем (end - start) для расчета длительности
                            let durationMs = endTime - startTime;

                            // Если время переходит через полночь (end < start), добавляем 24 часа
                            if (durationMs < 0) {
                                durationMs += 24 * 60 * 60 * 1000;
                            }

                            // Конвертируем в часы и обрезаем до целого количества часов
                            const durationHours = Math.floor(durationMs / (60 * 60 * 1000));

                            if (durationHours > 0) {
                                const slotCost = hourlyPrice * durationHours;
                                const baseSlotCost = hourlyBasePrice * durationHours;
                                totalCost += slotCost;
                                baseTotalCost += baseSlotCost;
                                breakdown.push({
                                    day: formatDateOnlyInRtbTZ(currentDate),
                                    hours: durationHours,
                                    partialCost: slotCost,
                                    periodIndex: periodIndex,
                                    timeSlot: `${this.formatTimeFromMs(startTime)} - ${this.formatTimeFromMs(endTime)}`
                                });
                            }
                        });
                    }
                }
            });

            return {
                totalCost: Math.round(totalCost * 100) / 100, // Округляем до 2 знаков
                baseTotalCost: Math.round(baseTotalCost * 100) / 100, // Базовая стоимость без наценки
                breakdown: breakdown
            };
        },
        getTotalCostForTariff(gid, tariff) {
            const calculationResult = this.calculateFrameCost(gid, tariff);
            return calculationResult.totalCost;
        },
        getFrameSize(gid) {
            const frame = this.frames.find((f) => f.gid === gid);
            return {
                width: frame?.width || '—',
                height: frame?.height || '—',
            };
        },
        isFrameActive(gid) {
            const frame = this.frames.find((f) => f.gid === gid);
            return frame ? frame.isActive : false;
        },
        formatTimeFromMs(ms) {
            const n = Number(ms);
            if (!Number.isFinite(n) || n < 0) return '00:00';
            if (n === 0) return '00:00';
            // Если время равно 24 часам (86400000 мс), показываем "24:00" вместо "00:00"
            if (n === 86400000) {
                return '24:00';
            }
            const totalSeconds = Math.floor(n / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        },
    },
};
</script>

<style scoped>
.tariff-select__header {
    font-weight: 600;
    margin-bottom: 8px;
}

.tariff-cards {
    display: flex;
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 10px;
}

.tariff-card {
    display: flex;
    width: 49%;
    gap: 10px;
    align-items: flex-start;
    padding: 10px;
    border: 1px solid #e6e9ef;
    border-radius: 8px;
    cursor: pointer;
}

.tariff-card__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.tariff-card__row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.tariff-price {
    font-weight: 700;
}

.tariff-badge {
    font-size: 12px;
    color: #0a7;
    border: 1px solid #0a7;
    padding: 2px 6px;
    border-radius: 999px;
}

.inactive-warning {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fef3c7;
    border: 1px solid #f59e0b;
    color: #92400e;
    padding: 12px;
    border-radius: 6px;
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 500;
}

.inactive-warning img {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}
</style>
